/**
 * In-memory ring buffer of recent browser-console output, attached to
 * app-feedback submissions so support can debug reports without asking the
 * user to open DevTools.
 *
 * Self-installing: importing this module patches `console.log/info/warn/
 * error/debug` (the originals keep working unchanged) and listens for
 * uncaught errors / unhandled promise rejections. It must therefore be
 * imported as early as possible in `main.tsx` — right after `./instrument` —
 * so module-eval logging of the rest of the app is captured too.
 */

export interface ConsoleLogEntry {
  level: string;
  /** ISO timestamp of when the entry was emitted. */
  timestamp: string;
  /** Serialised console arguments, joined with a space. */
  message: string;
}

const MAX_ENTRIES = 300;
const MAX_MESSAGE_LENGTH = 5000;
const MAX_ARG_LENGTH = 2000;

const buffer: ConsoleLogEntry[] = [];
let installed = false;

function serializeArg(arg: unknown): string {
  if (typeof arg === 'string') return arg;
  if (arg instanceof Error) {
    return arg.stack ?? `${arg.name}: ${arg.message}`;
  }
  if (typeof arg === 'function') return `[function ${arg.name || 'anonymous'}]`;
  try {
    const json = JSON.stringify(arg);
    if (json !== undefined) return json.slice(0, MAX_ARG_LENGTH);
  } catch {
    // Circular structures etc. — fall through to String().
  }
  return String(arg);
}

function push(level: string, args: unknown[]) {
  const message = args.map(serializeArg).join(' ').slice(0, MAX_MESSAGE_LENGTH);
  buffer.push({ level, timestamp: new Date().toISOString(), message });
  if (buffer.length > MAX_ENTRIES) {
    buffer.splice(0, buffer.length - MAX_ENTRIES);
  }
}

function install() {
  if (installed) return;
  installed = true;

  const levels = ['log', 'info', 'warn', 'error', 'debug'] as const;
  for (const level of levels) {
    const original = console[level].bind(console);
    console[level] = (...args: unknown[]) => {
      try {
        push(level, args);
      } catch {
        // The buffer must never break the console.
      }
      original(...args);
    };
  }

  window.addEventListener('error', (event) => {
    push('error', [
      `Uncaught: ${event.message} (${event.filename ?? '?'}:${event.lineno ?? '?'})`,
    ]);
  });
  window.addEventListener('unhandledrejection', (event) => {
    push('error', ['Unhandled rejection:', event.reason]);
  });
}

/** Recent console entries, oldest first (newest last). Returns a copy. */
export function getConsoleLogBuffer(): ConsoleLogEntry[] {
  return [...buffer];
}

install();
